import * as React from 'react';
import PropTypes, { object } from 'prop-types';
import classnames from 'classnames';

import {
    makeStyles,
    Slider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';

import { hasValue } from '../../youwell-common/objectUtils';

const useStyles = makeStyles(theme => ({
    root: {
        margin: theme.spacing(0, 4),

        [theme.breakpoints.down('xs')]: {
            margin: theme.spacing(0, 1),
        },
    },
    slider: {
        [theme.breakpoints.down('xs')]: {
            paddingBottom: theme.spacing(1),
        },
    },
    rail: {
        height: 4,
        position: 'absolute',
    },
    track: {
        height: 4,
        backgroundColor: theme.palette.secondary.main,
    },
    mark: {
        width: 12,
        height: 12,
        borderRadius: '100%',
        border: theme.youwell.borders.primary,
        backgroundColor: theme.palette.common.white,
        top: 7,
        marginLeft: -7, // NB: needed to position correctly in regards to thumb.
    },
    // TODO: .MuiSlider-root setter på padding... skjer kun ved klikk på select i inspect. Fungerer ikke nå ipad, har det gjort det tidligere? -ja men da har ikke markersene vært synlige
    //  Ved klikk på select igjen er stylingen slik den skal...
    markActive: {
        opacity: 1, // needed to override default style set by Slider.
        backgroundColor: theme.palette.secondary.main,
    },
    noUserInteraction: {
        opacity: 1, // needed to override default style set by Slider.
    },
    showThumb: {
        display: 'block',
        width: 15,
        height: 15,
        border: theme.youwell.borders.primary,
        backgroundColor: theme.palette.secondary.main,
    },
    hideThumb: {
        display: 'none',
        width: 15,
        height: 15,
    },
    topLabel: {
        '&:empty:before': {
            content: '"\\200b"', // unicode zero width space character
        },
    },
    labels: {
        display: 'none',
        justifyContent: 'space-between',

        [theme.breakpoints.down('xs')]: {
            display: 'flex',
        },
    },
}), { name: 'YouwellSlider' });


const YouwellSlider = ({
    value, onChange, topLabelFormat = null, className, marks, ...rest
}) => {
    const classes = useStyles();
    const saveTimer = React.useRef();
    const [changeId, setChangeId] = React.useState(null);
    const [sliderValue, setSliderValue] = React.useState();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const handleSliderChange = (event, sValue) => {
        setSliderValue(sValue);
        setChangeId(Date.now());
    };

    React.useEffect(() => {
        if (changeId) {
            if (saveTimer.current) {
                clearTimeout(saveTimer.current);
            }

            saveTimer.current = setTimeout(() => {
                onChange({ target: { value: sliderValue } }, sliderValue); // match Material Slider onChange = (event, value)
                setChangeId(prevId => (prevId === changeId ? null : prevId));
            }, 200);
        }
    }, [changeId, onChange, sliderValue]); // TODO: onChange-function "changes" on each parent-render so this fires a bit to often..

    React.useEffect(() => {
        if (!changeId) {
            if (hasValue(value)) {
                setSliderValue(Number(value));
            } else {
                setSliderValue(null);
            }
        }
    }, [changeId, value]);

    return (
        <div className={classnames(classes.root, className)}>
            {topLabelFormat && (
                <Typography className={classes.topLabel}>
                    {topLabelFormat(sliderValue)}
                </Typography>
            )}
            <Slider
                value={sliderValue || 0}
                className={classes.slider}
                classes={{
                    thumb: hasValue(sliderValue) ? classes.showThumb : classes.hideThumb, mark: classes.mark, markActive: hasValue(sliderValue) ? classes.markActive : classes.noUserInteraction, rail: classes.rail, track: classes.track,
                }}
                onChange={handleSliderChange}
                marks={isMobile ? null : marks}
                {...rest}
            />
            {marks?.length > 1 && (
                <div className={classes.labels}>
                    <Typography variant="caption">{marks[0].label}</Typography>
                    <Typography variant="caption">{marks[marks.length - 1].label}</Typography>
                </div>
            )}
        </div>
    );
};

YouwellSlider.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func.isRequired,
    topLabelFormat: PropTypes.func,
    className: PropTypes.string,
    marks: PropTypes.arrayOf(object), // { value, label }
};

YouwellSlider.defaultProps = {
    value: null,
    topLabelFormat: null,
    className: null,
    marks: [],
};

export default YouwellSlider;
